import { BaseService, ServiceResponse } from './base.service';
import type { 
  PerformanceRanking, 
  RankingEntry, 
  UserRanking,
  LeaderboardQuery
} from '../../types/performance-zones';

export class CourseLeaderboardService extends BaseService {

  // Get top 10 scores for a specific hole
  async getTop10ForHole(query: LeaderboardQuery): Promise<ServiceResponse<RankingEntry[]>> {
    try {
      if (!query.hole_number) {
        throw new Error('Hole number is required for hole leaderboards');
      }

      const gender = query.gender || 'overall';
      const handicapCategory = query.handicap_category || 'scratch';
      const limit = Math.min(query.limit || 10, 10); // Max 10 for top 10

      // Query the performance_rankings cache first for speed
      const { data: cachedData, error: cacheError } = await this.supabase
        .from('performance_rankings')
        .select(`
          *,
          profiles!inner(first_name, last_name, nickname)
        `)
        .eq('record_type', 'hole')
        .eq('reference_id', query.course_id)
        .eq('hole_number', query.hole_number)
        .eq('gender', gender)
        .eq('handicap_category', handicapCategory)
        .order('ranking_position', { ascending: true })
        .limit(limit);

      // If cache is empty or error, fall back to live query
      if (cacheError || !cachedData || cachedData.length === 0) {
        const { data: liveData, error: liveError } = await this.supabase
          .from('hole_records')
          .select(`
            best_score,
            user_id,
            round_id,
            achieved_at,
            tee_id,
            profiles!inner(first_name, last_name, nickname)
          `)
          .eq('course_id', query.course_id)
          .eq('hole_number', query.hole_number)
          .eq('gender', gender)
          .eq('record_type', handicapCategory)
          .order('best_score', { ascending: true })
          .order('achieved_at', { ascending: true }) // Tiebreaker: first to achieve
          .limit(limit);

        if (liveError) throw liveError;

        // Transform live data to RankingEntry format
        const rankings: RankingEntry[] = liveData?.map((record, index) => ({
          position: index + 1,
          user_id: record.user_id,
          score: record.best_score,
          user_name: record.profiles?.nickname || 
                    `${record.profiles?.first_name || ''} ${record.profiles?.last_name || ''}`.trim() ||
                    'Unknown Player',
          achieved_at: record.achieved_at,
          round_id: record.round_id,
          tee_id: record.tee_id
        })) || [];

        return this.handleResponse(rankings);
      }

      // Transform cached data to RankingEntry format
      const rankings: RankingEntry[] = cachedData.map(record => ({
        position: record.ranking_position,
        user_id: record.user_id,
        score: record.score,
        user_name: record.profiles?.nickname || 
                  `${record.profiles?.first_name || ''} ${record.profiles?.last_name || ''}`.trim() ||
                  'Unknown Player',
        achieved_at: record.achieved_at,
        round_id: record.round_id,
        tee_id: record.tee_id
      }));

      return this.handleResponse(rankings);
    } catch (error) {
      return this.handleResponse(null, error);
    }
  }

  // Get top 10 scores for an entire course
  async getTop10ForCourse(query: LeaderboardQuery): Promise<ServiceResponse<RankingEntry[]>> {
    try {
      const gender = query.gender || 'overall';
      const handicapCategory = query.handicap_category || 'scratch';
      const limit = Math.min(query.limit || 10, 10); // Max 10 for top 10

      // Query the performance_rankings cache first for speed
      const { data: cachedData, error: cacheError } = await this.supabase
        .from('performance_rankings')
        .select(`
          *,
          profiles!inner(first_name, last_name, nickname)
        `)
        .eq('record_type', 'course')
        .eq('reference_id', query.course_id)
        .is('hole_number', null) // Course records don't have hole_number
        .eq('gender', gender)
        .eq('handicap_category', handicapCategory)
        .order('ranking_position', { ascending: true })
        .limit(limit);

      // If cache is empty or error, fall back to live query
      if (cacheError || !cachedData || cachedData.length === 0) {
        const { data: liveData, error: liveError } = await this.supabase
          .from('course_records')
          .select(`
            best_total_score,
            user_id,
            round_id,
            achieved_at,
            tee_id,
            profiles!inner(first_name, last_name, nickname)
          `)
          .eq('course_id', query.course_id)
          .eq('gender', gender)
          .eq('record_type', handicapCategory)
          .order('best_total_score', { ascending: true })
          .order('achieved_at', { ascending: true }) // Tiebreaker: first to achieve
          .limit(limit);

        if (liveError) throw liveError;

        // Transform live data to RankingEntry format
        const rankings: RankingEntry[] = liveData?.map((record, index) => ({
          position: index + 1,
          user_id: record.user_id,
          score: record.best_total_score,
          user_name: record.profiles?.nickname || 
                    `${record.profiles?.first_name || ''} ${record.profiles?.last_name || ''}`.trim() ||
                    'Unknown Player',
          achieved_at: record.achieved_at,
          round_id: record.round_id,
          tee_id: record.tee_id
        })) || [];

        return this.handleResponse(rankings);
      }

      // Transform cached data to RankingEntry format
      const rankings: RankingEntry[] = cachedData.map(record => ({
        position: record.ranking_position,
        user_id: record.user_id,
        score: record.score,
        user_name: record.profiles?.nickname || 
                  `${record.profiles?.first_name || ''} ${record.profiles?.last_name || ''}`.trim() ||
                  'Unknown Player',
        achieved_at: record.achieved_at,
        round_id: record.round_id,
        tee_id: record.tee_id
      }));

      return this.handleResponse(rankings);
    } catch (error) {
      return this.handleResponse(null, error);
    }
  }

  // Get user's ranking for a specific hole
  async getUserRankingForHole(
    userId: string, 
    courseId: string, 
    holeNumber: number,
    gender: 'male' | 'female' | 'overall' = 'overall',
    handicapCategory: 'scratch' | 'net' = 'scratch'
  ): Promise<ServiceResponse<UserRanking | null>> {
    try {
      // First check if user has a score for this hole
      const { data: userScore, error: userError } = await this.supabase
        .from('hole_records')
        .select('best_score, achieved_at')
        .eq('user_id', userId)
        .eq('course_id', courseId)
        .eq('hole_number', holeNumber)
        .eq('gender', gender)
        .eq('record_type', handicapCategory)
        .single();

      if (userError && userError.code !== 'PGRST116') {
        throw userError;
      }

      if (!userScore) {
        return this.handleResponse(null); // User has no score for this hole
      }

      // Get all scores for this hole to calculate ranking
      const { data: allScores, error: scoresError } = await this.supabase
        .from('hole_records')
        .select('best_score, achieved_at, user_id')
        .eq('course_id', courseId)
        .eq('hole_number', holeNumber)
        .eq('gender', gender)
        .eq('record_type', handicapCategory)
        .order('best_score', { ascending: true })
        .order('achieved_at', { ascending: true });

      if (scoresError) throw scoresError;

      // Calculate user's position
      let position = 1;
      const totalCompetitors = allScores?.length || 0;

      for (const score of allScores || []) {
        if (score.user_id === userId) {
          break;
        }
        if (score.best_score < userScore.best_score || 
           (score.best_score === userScore.best_score && score.achieved_at < userScore.achieved_at)) {
          position++;
        }
      }

      const ranking: UserRanking = {
        user_id: userId,
        course_id: courseId,
        hole_number: holeNumber,
        current_position: position,
        score: userScore.best_score,
        total_competitors: totalCompetitors,
        record_type: handicapCategory,
        gender: gender,
        achieved_at: userScore.achieved_at
      };

      return this.handleResponse(ranking);
    } catch (error) {
      return this.handleResponse(null, error);
    }
  }

  // Get user's ranking for an entire course
  async getUserRankingForCourse(
    userId: string, 
    courseId: string,
    gender: 'male' | 'female' | 'overall' = 'overall',
    handicapCategory: 'scratch' | 'net' = 'scratch'
  ): Promise<ServiceResponse<UserRanking | null>> {
    try {
      // First check if user has a score for this course
      const { data: userScore, error: userError } = await this.supabase
        .from('course_records')
        .select('best_total_score, achieved_at')
        .eq('user_id', userId)
        .eq('course_id', courseId)
        .eq('gender', gender)
        .eq('record_type', handicapCategory)
        .single();

      if (userError && userError.code !== 'PGRST116') {
        throw userError;
      }

      if (!userScore) {
        return this.handleResponse(null); // User has no score for this course
      }

      // Get all scores for this course to calculate ranking
      const { data: allScores, error: scoresError } = await this.supabase
        .from('course_records')
        .select('best_total_score, achieved_at, user_id')
        .eq('course_id', courseId)
        .eq('gender', gender)
        .eq('record_type', handicapCategory)
        .order('best_total_score', { ascending: true })
        .order('achieved_at', { ascending: true });

      if (scoresError) throw scoresError;

      // Calculate user's position
      let position = 1;
      const totalCompetitors = allScores?.length || 0;

      for (const score of allScores || []) {
        if (score.user_id === userId) {
          break;
        }
        if (score.best_total_score < userScore.best_total_score || 
           (score.best_total_score === userScore.best_total_score && score.achieved_at < userScore.achieved_at)) {
          position++;
        }
      }

      const ranking: UserRanking = {
        user_id: userId,
        course_id: courseId,
        current_position: position,
        score: userScore.best_total_score,
        total_competitors: totalCompetitors,
        record_type: handicapCategory,
        gender: gender,
        achieved_at: userScore.achieved_at
      };

      return this.handleResponse(ranking);
    } catch (error) {
      return this.handleResponse(null, error);
    }
  }

  // Update the rankings cache (usually called after new rounds)
  async updateRankingsCache(courseId?: string): Promise<ServiceResponse<{ updated: number }>> {
    try {
      let updatedCount = 0;

      // If specific course provided, update only that course
      if (courseId) {
        await this.updateCourseRankingsCache(courseId);
        await this.updateHoleRankingsCache(courseId);
        updatedCount = 1;
      } else {
        // Update all courses - this should be run periodically
        const { data: courses } = await this.supabase
          .from('courses')
          .select('id');

        for (const course of courses || []) {
          await this.updateCourseRankingsCache(course.id);
          await this.updateHoleRankingsCache(course.id);
          updatedCount++;
        }
      }

      return this.handleResponse({ updated: updatedCount });
    } catch (error) {
      return this.handleResponse(null, error);
    }
  }

  // Private helper to update course rankings cache
  private async updateCourseRankingsCache(courseId: string) {
    const genders = ['male', 'female', 'overall'];
    const categories = ['scratch', 'net'];

    for (const gender of genders) {
      for (const category of categories) {
        // Get top 10 scores for this combination
        const { data: topScores } = await this.supabase
          .from('course_records')
          .select('*')
          .eq('course_id', courseId)
          .eq('gender', gender)
          .eq('record_type', category)
          .order('best_total_score', { ascending: true })
          .order('achieved_at', { ascending: true })
          .limit(10);

        if (topScores && topScores.length > 0) {
          // Clear existing cache for this combination
          await this.supabase
            .from('performance_rankings')
            .delete()
            .eq('record_type', 'course')
            .eq('reference_id', courseId)
            .eq('gender', gender)
            .eq('handicap_category', category);

          // Insert new rankings
          const rankings = topScores.map((score, index) => ({
            record_type: 'course' as const,
            reference_id: courseId,
            hole_number: null,
            gender: gender as 'male' | 'female' | 'overall',
            handicap_category: category as 'scratch' | 'net',
            ranking_position: index + 1,
            score: score.best_total_score,
            user_id: score.user_id,
            round_id: score.round_id,
            achieved_at: score.achieved_at,
            tee_id: score.tee_id
          }));

          await this.supabase
            .from('performance_rankings')
            .insert(rankings);
        }
      }
    }
  }

  // Private helper to update hole rankings cache
  private async updateHoleRankingsCache(courseId: string) {
    const genders = ['male', 'female', 'overall'];
    const categories = ['scratch', 'net'];

    // Update each hole (1-18)
    for (let holeNumber = 1; holeNumber <= 18; holeNumber++) {
      for (const gender of genders) {
        for (const category of categories) {
          // Get top 10 scores for this hole
          const { data: topScores } = await this.supabase
            .from('hole_records')
            .select('*')
            .eq('course_id', courseId)
            .eq('hole_number', holeNumber)
            .eq('gender', gender)
            .eq('record_type', category)
            .order('best_score', { ascending: true })
            .order('achieved_at', { ascending: true })
            .limit(10);

          if (topScores && topScores.length > 0) {
            // Clear existing cache for this combination
            await this.supabase
              .from('performance_rankings')
              .delete()
              .eq('record_type', 'hole')
              .eq('reference_id', courseId)
              .eq('hole_number', holeNumber)
              .eq('gender', gender)
              .eq('handicap_category', category);

            // Insert new rankings
            const rankings = topScores.map((score, index) => ({
              record_type: 'hole' as const,
              reference_id: courseId,
              hole_number: holeNumber,
              gender: gender as 'male' | 'female' | 'overall',
              handicap_category: category as 'scratch' | 'net',
              ranking_position: index + 1,
              score: score.best_score,
              user_id: score.user_id,
              round_id: score.round_id,
              achieved_at: score.achieved_at,
              tee_id: score.tee_id
            }));

            await this.supabase
              .from('performance_rankings')
              .insert(rankings);
          }
        }
      }
    }
  }

  // Get user's top 10 finishes across all courses/holes
  async getUserTop10Finishes(
    userId: string,
    filters?: {
      record_type?: 'hole' | 'course';
      gender?: 'male' | 'female' | 'overall';
      handicap_category?: 'scratch' | 'net';
      limit?: number;
    }
  ): Promise<ServiceResponse<PerformanceRanking[]>> {
    try {
      // First, get the rankings data
      let query = this.supabase
        .from('performance_rankings')
        .select('*')
        .eq('user_id', userId)
        .lte('ranking_position', 10); // Only top 10 finishes

      if (filters?.record_type) {
        query = query.eq('record_type', filters.record_type);
      }
      if (filters?.gender) {
        query = query.eq('gender', filters.gender);
      }
      if (filters?.handicap_category) {
        query = query.eq('handicap_category', filters.handicap_category);
      }

      query = query
        .order('ranking_position', { ascending: true })
        .order('achieved_at', { ascending: false });

      if (filters?.limit) {
        query = query.limit(filters.limit);
      }

      const { data: rankingsData, error: rankingsError } = await query;

      if (rankingsError) throw rankingsError;

      if (!rankingsData || rankingsData.length === 0) {
        return this.handleResponse([]);
      }

      // Get unique course IDs from the rankings
      const courseIds = [...new Set(rankingsData.map(ranking => ranking.reference_id))];

      // Fetch course names for those IDs
      const { data: coursesData, error: coursesError } = await this.supabase
        .from('courses')
        .select('id, name')
        .in('id', courseIds);

      if (coursesError) throw coursesError;

      // Create a map of course ID to course name
      const courseNameMap = new Map(
        coursesData?.map(course => [course.id, course.name]) || []
      );

      // Transform data to include course name
      const transformedData: PerformanceRanking[] = rankingsData.map(ranking => ({
        ...ranking,
        course_name: courseNameMap.get(ranking.reference_id) || 'Unknown Course'
      }));

      return this.handleResponse(transformedData);
    } catch (error) {
      return this.handleResponse(null, error);
    }
  }
}